/**
 * Enhanced App Component with 3D Visualization
 * @license Apache-2.0
 */

import React, { useRef, useState, useEffect } from "react";
import "../../App.scss";
import { Altair } from "../altair/Altair";
import ADHDAssistant from "../adhd-assistant/ADHDAssistant";
import { DevAuthGuard } from "../auth/DevAuthGuard";
import { ADHDErrorBoundary } from "../error-boundary/ADHDErrorBoundary";
import { FocusManagerProvider } from "../accessibility/FocusManager";
import { SettingsProvider } from "../../contexts/SettingsContext";
import { SubscriptionProvider } from "../../contexts/SubscriptionContext";
import { UsageTrackingProvider } from "../../contexts/UsageTrackingContext";
import SettingsPage from "../settings/SettingsPage";
import { UsageTracker } from "../usage-tracker";
import { ToastProvider } from "../toast-notification";
import { timeService } from "../../lib/time-service";
import cn from "classnames";
import { EnhancedLiveAPIProvider } from "../../contexts/EnhancedLiveAPIContext";
// import SubscriptionTestPage from "../../pages/SubscriptionTestPage";
import "../../lib/i18n";

function EnhancedApp() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  // Initialize the time service on app startup
  useEffect(() => {
    timeService.initialize().catch(error => {
      console.warn("Failed to initialize time service:", error);
    });
  }, []);

  return (
    <FocusManagerProvider>
      <div className="App enhanced-app">
        <ADHDErrorBoundary level="page">
          <DevAuthGuard>
            <EnhancedAppContent
              videoRef={videoRef}
              videoStream={videoStream}
              setVideoStream={setVideoStream}
            />
          </DevAuthGuard>
        </ADHDErrorBoundary>
      </div>
    </FocusManagerProvider>
  );
}

// Navigation context type
type AppView = "main" | "settings";

function EnhancedAppContent({
  videoRef,
  videoStream,
  setVideoStream,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  setVideoStream: (stream: MediaStream | null) => void;
}) {
  const [currentView, setCurrentView] = useState<AppView>("main");

  // Check URL for test mode
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get("test") === "subscription";

  // Show test page if in test mode
  if (testMode) {
    return (
      <div className="test-mode">
        <div
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            zIndex: 1000,
            display: "flex",
            gap: "10px",
          }}
        >
          <button
            onClick={() => (window.location.href = window.location.pathname)}
            style={{
              padding: "8px 16px",
              background: "#6b7280",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Back to App
          </button>
        </div>
        {/* <SubscriptionTestPage /> */}
        <div>Subscription Test Page</div>
      </div>
    );
  }

  const renderMainView = () => (
    <main id="main-content" className="modern-unified-layout enhanced-layout">
      {/* Usage Tracker at the top */}
      <ADHDErrorBoundary level="component" retryable={true}>
        <UsageTracker />
      </ADHDErrorBoundary>

      {/* APP goes here */}
      <ADHDErrorBoundary level="feature" retryable={true}>
        <div id="adhd-assistant">
          <ADHDAssistant
            videoRef={videoRef}
            videoStream={videoStream}
            onVideoStreamChange={setVideoStream}
            onNavigateToSettings={() => setCurrentView("settings")}
          />
        </div>
      </ADHDErrorBoundary>

      <ADHDErrorBoundary level="component" retryable={true}>
        <Altair />
      </ADHDErrorBoundary>

      <video
        className={cn("stream", {
          hidden: !videoRef.current || !videoStream,
        })}
        ref={videoRef}
        autoPlay
        playsInline
      />
    </main>
  );

  const renderSettingsView = () => (
    <main id="main-content" className="settings-view-layout">
      <SettingsProvider>
        <SettingsPage onNavigateBack={() => setCurrentView("main")} />
      </SettingsProvider>
    </main>
  );

  const wrappedContent = <>{currentView === "main" ? renderMainView() : renderSettingsView()}</>;

  // Enhanced API options with 3D visualization
  const enhancedAPIOptions = {
    apiKey: "dummy-key-for-ephemeral-tokens", // This will be replaced by ephemeral tokens
    model: "gemini-2.5-flash-preview-native-audio-dialog",
    visualization3D: true,
  };

  return (
    <SubscriptionProvider>
      <UsageTrackingProvider>
        <ToastProvider position="top-right">
          <EnhancedLiveAPIProvider options={enhancedAPIOptions}>
            {wrappedContent}
          </EnhancedLiveAPIProvider>
        </ToastProvider>
      </UsageTrackingProvider>
    </SubscriptionProvider>
  );
}

export default EnhancedApp;
